import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Header from "../../../header/header.js";
import Navvbar from "../../../header/Navvbar.js";
import ReportParameters from './NewCases.js';
import { PiFolderStarBold } from "react-icons/pi";
import axios from 'axios';

const Report = () => {
  const location = useLocation();

  // Read selectedDate from Home.js
  const { selectedDate, cases: preFilteredCases = [], labFilter } = location.state || {};

  const filters = location.state?.formData || null;
  const [showParams, setShowParams] = useState(false);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add state for lab user info
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [labAccounts, setLabAccounts] = useState([]);
  const [authLoaded, setAuthLoaded] = useState(false);

  useEffect(() => {
    if (filters) {
      setShowParams(false);
    }
  }, [filters]);

  // Load user type & labId from localStorage
  useEffect(() => {
    console.log('=== Reportresult: Loading from localStorage ===');

    let storedLabId = localStorage.getItem('userLabId');
    let storedUserType = localStorage.getItem('userType');

    // Check sessionStorage as fallback
    if (!storedLabId) {
      storedLabId = sessionStorage.getItem('userLabId');
    }

    if (!storedUserType) {
      storedUserType = sessionStorage.getItem('userType');
    }

    // Also check for user data object
    const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);

        if (!storedLabId && userData.labId) {
          storedLabId = userData.labId;
        }

        if (!storedUserType && userData.userType) {
          storedUserType = userData.userType;
        }
      } catch (e) {
        console.error('Error parsing userData:', e);
      }
    }

    // Set the state
    if (storedLabId) {
      setLabId(storedLabId);
    }

    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      // If labId exists but no userType, default to 'lab'
      setUserType('lab');
    } else {
      // If nothing found, default to 'admin'
      setUserType('admin');
    }

    // Mark auth as loaded
    setAuthLoaded(true);
    console.log('Reportresult - UserType:', storedUserType, 'LabId:', storedLabId);
  }, []);

  // Fetch lab accounts if lab user
  useEffect(() => {
    if (!authLoaded || userType !== 'lab' || !labId) return;

    const fetchLabAccounts = async () => {
      try {
        console.log(`Reportresult: Fetching accounts for lab ${labId}...`);
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
        );

        if (response.data && response.data.success) {
          const accounts = response.data.data || [];
          setLabAccounts(accounts);
          console.log(`Reportresult: Found ${accounts.length} accounts for lab ${labId}`);
        } else {
          console.log('Reportresult: No lab accounts found');
          setLabAccounts([]);
        }
      } catch (error) {
        console.error('Reportresult: Error fetching lab accounts:', error);
        setLabAccounts([]);
      }
    };

    fetchLabAccounts();
  }, [authLoaded, userType, labId]);

  // Format lab ID to 8 digits with leading zeros
  const formatLabId = (id) => {
    if (!id) return '00000000';
    const idStr = String(id);
    if (/^\d+$/.test(idStr)) {
      return idStr.padStart(8, '0');
    }
    return idStr;
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  // Fetch cases from API
  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/cases/cases');

        // Ensure response.data is an array
        let fetchedCases = [];
        if (Array.isArray(response.data)) {
          fetchedCases = response.data;
        } else if (response.data && typeof response.data === 'object') {
          if (Array.isArray(response.data.cases)) {
            fetchedCases = response.data.cases;
          } else if (Array.isArray(response.data.data)) {
            fetchedCases = response.data.data;
          } else {
            fetchedCases = [response.data];
          }
        }

        // Filter cases based on user type and lab accounts
        if (userType === 'lab' && labId && labAccounts.length > 0) {
          const labAccountIds = labAccounts.map(acc => acc.accountId).filter(id => id);
          console.log(`Reportresult: Filtering cases for lab ${labId}, account IDs:`, labAccountIds);

          fetchedCases = fetchedCases.filter(caseItem =>
            caseItem && caseItem.accountId && labAccountIds.includes(caseItem.accountId)
          );
          console.log(`Reportresult: Found ${fetchedCases.length} cases for lab ${labId}`);
        }

        setCases(fetchedCases);
        setLoading(false);
      } catch (err) {
        setError('Failed to load cases');
        setLoading(false);
        console.error(err);
      }
    };

    if (authLoaded) {
      fetchCases();
    }
  }, [authLoaded, labAccounts, userType, labId]);

  // Filter cases by selectedDate if coming from Home
  const filteredCases = useMemo(() => {
    if (!Array.isArray(cases)) {
      console.log('Reportresult: cases is not an array, returning empty array');
      return [];
    }

    let result = cases;

    // Agar Home se selectedDate aaya hai → sirf us date ke cases dikhao
    if (selectedDate) {
      result = cases.filter(c => {
        if (!c || !c.date) return false;
        try {
          const caseDate = new Date(c.date);
          const [y, m, d] = selectedDate.split('-').map(Number);
          return caseDate.getFullYear() === y &&
                 caseDate.getMonth() === m - 1 &&
                 caseDate.getDate() === d;
        } catch (error) {
          console.error('Error parsing date:', error);
          return false;
        }
      });
      console.log(`Reportresult: Cases filtered for date ${selectedDate}: ${result.length}`);
    }
    // Agar filters (form se) aaye hain → unke hisaab se filter karo
    else if (filters) {
      result = cases.filter(c => {
        if (!c || !c.createdAt) return false;

        try {
          const createdAt = new Date(c.createdAt);
          const start = new Date(filters.creationStartDate);
          const end = new Date(filters.creationEndDate);
          end.setHours(23, 59, 59, 999);

          if (createdAt < start || createdAt > end) return false;
          if (filters.groupBy && c.account_name !== filters.groupBy) return false;

          if (filters.procedures && c.procedures) {
            const hasProcedure = Array.isArray(c.procedures)
              ? c.procedures.some(p => p && p.procedure === filters.procedures)
              : false;
            if (!hasProcedure) return false;
          }

          return true;
        } catch (error) {
          console.error('Error filtering case:', error);
          return false;
        }
      });
    }

    return result;
  }, [cases, selectedDate, filters]);

  // Group filtered cases by account_name
  const groupedCases = useMemo(() => {
    const grouped = {};

    if (!Array.isArray(filteredCases)) {
      console.log('Reportresult: filteredCases is not an array');
      return grouped;
    }

    filteredCases.forEach(c => {
      if (!c) return;
      const account = c.account_name || 'Unknown Account';
      if (!grouped[account]) {
        grouped[account] = [];
      }
      grouped[account].push(c);
    });
    return grouped;
  }, [filteredCases]);

  // Calculate total units for a case from procedures array
  const calculateTotalUnits = (caseData) => {
    if (!caseData || !caseData.procedures || !Array.isArray(caseData.procedures)) {
      return 0;
    }

    return caseData.procedures.reduce((total, procedure) => {
      return total + (procedure.calculatedUnits || 0);
    }, 0);
  };

  // Format procedures and units for display
  const formatProceduresAndUnits = (caseData) => {
    if (!caseData || !caseData.procedures || !Array.isArray(caseData.procedures)) {
      return { procedures: '', units: '' };
    }

    const procedureNames = caseData.procedures.map(p => p && p.procedure ? p.procedure : 'Unknown Procedure');
    const procedureUnits = caseData.procedures.map(p => p && p.calculatedUnits ? p.calculatedUnits : 0);

    return {
      procedures: procedureNames.join(', '),
      units: procedureUnits.join(', ')
    };
  };

  // Calculate Summary
  const summary = useMemo(() => {
    if (!Array.isArray(filteredCases)) {
      return { cases: 0, estimatedValue: '£0.00', units: 0 };
    }

    const totalCases = filteredCases.length;
    const totalValue = filteredCases.reduce((sum, c) => {
      if (!c) return sum;
      return sum + (c.totalAmount || 0);
    }, 0);

    const totalUnits = filteredCases.reduce((sum, c) => {
      if (!c) return sum;
      return sum + calculateTotalUnits(c);
    }, 0);

    return {
      cases: totalCases,
      estimatedValue: `£${totalValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`,
      units: totalUnits
    };
  }, [filteredCases]);

  // Title based on source
  const pageTitle = selectedDate
    ? `New Cases ${selectedDate} `
    : filters
    ? `New Cases ${filters.creationStartDate} to ${filters.creationEndDate} ${userType === 'lab' ? `(Lab ${formatLabId(labId)})` : ''}`
    : `All Cases - No Filters Applied ${userType === 'lab' ? `(Lab ${formatLabId(labId)})` : ''}`;

  if (loading) return <div className="rr-container">Loading cases...</div>;
  if (error) return <div className="rr-container" style={{ color: 'red' }}>{error}</div>;

  return (
    <>
      <Header />
      <Navvbar />

      <div className="rr-container">
        {/* Header */}
        <div className="rr-header">
          <h2>{pageTitle}</h2>
          <button
            className="rr-params-button"
            onClick={() => setShowParams(!showParams)}
          >
            Parameters
          </button>
        </div>

        {/* Parameters Section */}
        {showParams && (
          <div className="rr-params-tool">
            <ReportParameters />
          </div>
        )}

        {/* Summary Section */}
        <div className="rr-summary-section">
          <h3 className="rr-section-title">Summary</h3>
          <table className="rr-summary-table">
            <thead>
              <tr className="rr-summary-header-row">
                <th>Cases</th>
                <th>Estimated Value</th>
                <th> </th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr className="rr-summary-bg-row">
                <td>{summary.cases}</td>
                <td>{summary.estimatedValue}</td>
                <td></td>
                <td>{summary.units}</td>
              </tr>
              <tr className="rr-blank-row"><td colSpan="4"></td></tr>
            </tbody>
          </table>
        </div>
        <div style={{ backgroundColor: '#f4f4f4', padding: '10px', borderTop: '2px solid #d6cfcfff', marginTop: '-20px' }}></div>

        {/* Cases Section */}
        <div className="rr-cases-section">
          <h3 className="rr-section-title">Cases</h3>
          <table className="rr-cases-table">
            <thead>
              <tr className="rr-cases-header-row">
                <th>Account</th>
                <th>Case ID</th>
                <th>Patient</th>
                <th>Estimated Value</th>
                <th>Tags</th>
                <th>On-Hold</th>
                <th>Procedure(s)</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(groupedCases).length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                    {userType === 'lab'
                      ? `No cases found for your lab (Lab ${formatLabId(labId)}) ${selectedDate ? `on ${selectedDate}` : 'with selected filters'}.`
                      : `No cases found for ${selectedDate || 'selected filters'}.`}
                  </td>
                </tr>
              ) : (
                Object.keys(groupedCases).map((account, idx) => (
                  <React.Fragment key={idx}>
                    <tr className="rr-account-row">
                      <td colSpan="8">
                        <a href={`/account/${account.toLowerCase().replace(/\s+/g, '-')}`} className="rr-account-link">
                          {account}
                        </a>
                      </td>
                    </tr>
                    {groupedCases[account].map((c, i) => {
                      if (!c) return null;
                      const { procedures, units } = formatProceduresAndUnits(c);
                      return (
                        <tr key={c._id || i} className="rr-case-row">
                          <td></td>
                          <td className="rr-case-details">
                            <a href={`/case/${c.case_id}`}>
                              <span className="rr-case-icon">
                                <PiFolderStarBold />
                              </span>
                              {c.case_id || 'N/A'}
                            </a>
                          </td>
                          <td>
                            <a href={`/patient/${(c.first_name || '').toLowerCase()}-${(c.last_name || '').toLowerCase()}`} className="rr-patient-link">
                              {(c.first_name || '')} {(c.last_name || '')}
                            </a>
                          </td>
                          <td>{formatCurrency(c.totalAmount)}</td>
                          <td>{Array.isArray(c.tags) ? c.tags.join(', ') : ''}</td>
                          <td>{c.onHoldReason ? 'Yes' : ''}</td>
                          <td>{procedures}</td>
                          <td>{units}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* All selectors are prefixed with .rr- and scoped under .rr-container,
   so nothing leaks into Header/Navvbar or other pages. */
const styles = `
.rr-container {
  font-family: Arial, sans-serif;
  padding: 20px 30px;
  width: 100%;
  max-width: 1730px;
  margin: 0 auto;
  box-sizing: border-box;
}
.rr-container *,
.rr-container *::before,
.rr-container *::after {
  box-sizing: border-box;
}
.rr-container .rr-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  flex-wrap: wrap;
  gap: 10px;
}
.rr-container .rr-header h2 {
  margin: 0;
  font-size: 24px;
  color: #000;
}
.rr-container .rr-params-button {
  background-color: #004aad;
  border: 1px solid #fff;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
}
.rr-container .rr-params-button:hover {
  background-color: #003a8c;
}

/* ---- Parameters panel (wraps NewCases.js) ---- */
.rr-container .rr-params-tool {
  width: 100%;
  max-width: 100%;
  padding: 0 0 20px 20px;
  margin-bottom: 10px;
  overflow: visible;
}
.rr-container .rr-params-tool h2 {
  margin-left: 0 !important;
  padding-left: 0 !important;
}
.rr-container .rr-params-tool > div {
  margin-left: 0 !important;
  width: 100%;
}
.rr-container .rr-params-tool select[size] {
  height: auto;
  max-height: 230px;
  overflow-y: auto;
}
.rr-container .rr-params-tool input,
.rr-container .rr-params-tool select {
  min-width: 0;
}

.rr-container .rr-section-title {
  font-size: 18px;
  padding: 10px 0;
  margin: 20px 0 0;
  color: #004aad;
}
.rr-container .rr-summary-table,
.rr-container .rr-cases-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: -3px;
  table-layout: auto;
}
.rr-container .rr-summary-table th,
.rr-container .rr-summary-table td,
.rr-container .rr-cases-table th,
.rr-container .rr-cases-table td {
  padding: 12px 10px;
  text-align: left;
  color: #000;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.rr-container .rr-summary-header-row th,
.rr-container .rr-cases-header-row th {
  border-top: 2px solid #004aad;
  background-color: #f4f4f4;
  font-weight: bold;
  white-space: nowrap;
  font-size: 14px;
}
.rr-container .rr-blank-row td {
  height: 10px;
  background-color: #fff;
  padding: 0;
}
.rr-container .rr-case-row td {
  border-top: 2px solid #eee;
  font-size: 14px;
}
.rr-container .rr-cases-table .rr-case-details a {
  text-decoration: none;
  color: #004aad;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.rr-container .rr-cases-table .rr-case-icon {
  margin-right: 5px;
  display: inline-flex;
  align-items: center;
}
.rr-container .rr-cases-table .rr-case-icon svg {
  font-size: 16px;
  color: #000;
}
.rr-container .rr-account-link,
.rr-container .rr-patient-link {
  color: #004aad !important;
  text-decoration: none !important;
}
.rr-container .rr-account-link:hover,
.rr-container .rr-patient-link:hover {
  color: #003a8c !important;
  text-decoration: underline !important;
}
.rr-container .rr-account-row td {
  padding-top: 18px;
  padding-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #e0e0e0;
}
.rr-container .rr-cases-section {
  width: 100%;
  overflow-x: auto;
}
.rr-container .rr-cases-table {
  min-width: 900px;
}
.rr-container .rr-summary-section {
  width: 100%;
}
.rr-container .rr-summary-table {
  min-width: 500px;
}
`;

function Reportresult() {
  return (
    <div>
      <style>{styles}</style>
      <Report />
    </div>
  );
}

export default Reportresult;